import React, { useCallback, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { BiSolidMessageSquareDetail } from "react-icons/bi";
import { BiSort } from "react-icons/bi";
import { Comment, VideoAnnotation } from "./types";
import { CommentItem } from "./CommentItem";
import { AddComment, AddCommentHandle } from "./AddComment";
import { Dropdown } from "../Dropdown";
import { LuMessageSquare } from "react-icons/lu";
import { PanelDragContext } from "../ResizeablePanel";

interface CommentsSectionProps {
    comments: Comment[];
    indexId: string;
    onSeek: (time: number) => void;
    onCommentSelect: (comment: Comment) => void;
    videoId: string;
    currentTime: number;
    isPlaying: boolean;
    setIsPlaying: (playing: boolean) => void;
    onCommentAdded: () => void;
    currentUserEmail: string;
    isLoading: boolean;
    readOnly: boolean;
    shareToken?: string;
    selectedCommentId: string | null;
    setSelectedCommentId: (id: string) => void;
    draftAnnotation: VideoAnnotation | null;
    annotationTime: number | null;
    onAnnotationCommentAdded: () => void;
    onAnnotationClear: () => void;
    composerRef?: React.Ref<AddCommentHandle>;
}

type SortOption = "latest" | "timeline";

const CommentsSection: React.FC<CommentsSectionProps> = ({
    comments: initialComments,
    indexId,
    onSeek,
    onCommentSelect,
    videoId,
    currentTime,
    isPlaying,
    setIsPlaying,
    onCommentAdded,
    currentUserEmail,
    isLoading,
    readOnly = true,
    shareToken,
    selectedCommentId,
    setSelectedCommentId,
    draftAnnotation,
    annotationTime,
    onAnnotationCommentAdded,
    onAnnotationClear,
    composerRef,
}) => {
    const [loadingReplies, setLoadingReplies] = useState<Record<string, boolean>>({});
    const [sortOption, setSortOption] = useState<SortOption>("timeline");
    const [comments, setComments] = useState<Comment[]>(initialComments);
    // const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
    const commentsContainerRef = useRef<HTMLDivElement>(null);
    const isPanelDragging = useContext(PanelDragContext);

    useEffect(() => {
        setComments(sortComments(initialComments, sortOption));
    }, [initialComments, sortOption]);

    useEffect(() => {
        if (selectedCommentId && commentsContainerRef.current) {
            const selectedElement = commentsContainerRef.current.querySelector(
                `[data-comment-id="${selectedCommentId}"]`
            );

            if (selectedElement) {
                selectedElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
        }
    }, [selectedCommentId]);

    const sortComments = (commentsToSort: Comment[], option: SortOption): Comment[] => {
        const sortedComments = [...commentsToSort];

        const topLevelComments = sortedComments.filter(comment => !comment.parent_comment);
        const replyComments = sortedComments.filter(comment => comment.parent_comment);

        const sortedTopLevel = [...topLevelComments].sort((a, b) => {
            switch (option) {
                case "latest":
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                case "timeline":
                    return a.start_time - b.start_time;
                default:
                    return 0;
            }
        });

        return [...sortedTopLevel, ...replyComments];
    };

    const getHeaders = useCallback(() => {
        if (shareToken) {
            return { "X-Share-Token": shareToken };
        }
        const token = localStorage.getItem("token");
        return { Authorization: `Bearer ${token}` };
    }, [shareToken]);

    const fetchReplies = useCallback(
        async (commentId: string): Promise<Comment | null> => {
            try {
                setLoadingReplies(prev => ({ ...prev, [commentId]: true }));
                const response = await axios.get<Comment>(
                    `${process.env.REACT_APP_API}/api/quick-review/comments/${videoId}/${commentId}`,
                    { headers: getHeaders() }
                );
                return response.data;
            } catch (error) {
                console.error("Error fetching replies:", error);
                return null;
            } finally {
                setLoadingReplies(prev => ({ ...prev, [commentId]: false }));
            }
        },
        [videoId, getHeaders]
    );

    const sortingOptions = [
        {
            label: "Timecode (Default)",
            onClick: () => setSortOption("timeline"),
        },
        {
            label: "Latest Comments",
            onClick: () => setSortOption("latest"),
        },
    ];

    const commentCount = comments.filter(comment => !comment.parent_comment).length;

    return (
        <div className="relative h-full flex flex-col">
            <div className="bg-white border-b border-gray-200 px-4 py-2">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <h3 className="text-xl font-semibold">Comments</h3>
                        <span className="text-sm text-gray-500">({commentCount})</span>
                    </div>

                    <Dropdown
                        trigger={
                            <div className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                                <BiSort className="text-lg" />
                                <span className="text-sm">
                                    {sortOption === "timeline" ? "Timecode" : "Latest Comments"}
                                </span>
                            </div>
                        }
                        items={sortingOptions}
                        align="right"
                        triggerClassName="px-3 py-2 rounded-md hover:bg-gray-100"
                        menuClassName="w-40"
                    />
                </div>
            </div>

            <div
                ref={commentsContainerRef}
                className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4"
            >
                {isLoading ? (
                    <div className="flex justify-center items-center h-32">
                        <div className="w-8 h-8 border-4 border-t-purple-600 border-purple-200 rounded-full animate-spin" />
                    </div>
                ) : comments.length > 0 ? (
                    comments.map(comment => (
                        <div
                            key={comment.id}
                            data-comment-id={comment.id}
                            className="rounded-lg border border-gray-200 bg-white shadow-sm transition-colors duration-200 hover:border-gray-300"
                        >
                            <CommentItem
                                comment={comment}
                                indexId={indexId}
                                onSeek={onSeek}
                                onCommentSelect={onCommentSelect}
                                onCommentUpdate={onCommentAdded}
                                videoId={videoId}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                                currentTime={currentTime}
                                currentUserEmail={currentUserEmail}
                                fetchReplies={fetchReplies}
                                depth={0}
                                isLoadingReplies={loadingReplies[comment.id]}
                                readOnly={readOnly}
                                shareToken={shareToken}
                                selectedCommentId={selectedCommentId}
                                setSelectedCommentId={setSelectedCommentId}
                            />
                        </div>
                    ))
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 space-y-2">
                        <BiSolidMessageSquareDetail size={80} />
                        <p className="text-sm font-medium">
                            No comments yet. {!readOnly && "Be the first to comment!"}
                        </p>
                    </div>
                )}
            </div>

            <div className="bg-white border-t border-gray-200">
                {readOnly ? (
                    <div className="flex justify-center">
                        <p>You don't have permission to comment</p>
                    </div>
                ) : (
                    <AddComment
                        ref={composerRef}
                        videoId={videoId}
                        indexId={indexId}
                        currentTime={currentTime}
                        isPlaying={isPlaying}
                        setIsPlaying={setIsPlaying}
                        onCommentAdded={
                            draftAnnotation?.strokes.length
                                ? onAnnotationCommentAdded
                                : onCommentAdded
                        }
                        shareToken={shareToken}
                        currentUserEmail={currentUserEmail}
                        annotation={draftAnnotation}
                        annotationTime={annotationTime}
                        onAnnotationClear={onAnnotationClear}
                    />
                )}
            </div>
            <div
                className={`pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-base-100 transition-opacity duration-150 ${
                    isPanelDragging ? "opacity-100" : "opacity-0"
                }`}
            >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                    <LuMessageSquare className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-gray-500">Comments</span>
            </div>
        </div>
    );
};

export default CommentsSection;
